import { computeEdgePositions, entityCodesToText, getTransformAttr, } from "../utils.js";
import { cleanCSSValue } from "./cssUtils.js";
const parseSubGraph = (data, containerEl) => {
    // Extract only node id for better reference
    // e.g. full element id = "flowchart-c1-205" will map to "c1"
    const nodeIds = data.nodes.map((n) => {
        if (n.startsWith("flowchart-")) {
            return n.split("-")[1];
        }
        return n;
    });
    // Get position
    const el = containerEl.querySelector(`[id='${data.id}']`);
    if (!el) {
        throw new Error("SubGraph element not found");
    }
    const position = computeElementPosition(el, containerEl);
    // Get dimension
    const boundingBox = el.getBBox();
    const dimension = {
        width: boundingBox.width,
        height: boundingBox.height,
    };
    return {
        id: data.id,
        nodeIds,
        text: entityCodesToText(data.title),
        labelType: "text",
        ...position,
        ...dimension,
    };
};
const parseVertex = (vertex, containerEl, classes) => {
    // Find Vertex element
    const node = containerEl.querySelector(`[id*="${vertex.domId}"]`);
    if (!node) {
        return undefined;
    }
    // Check if Vertex attached with link
    let link;
    if (node.parentElement?.tagName.toLowerCase() === "a") {
        link = node.parentElement.getAttribute("xlink:href");
    }
    // Get position
    const position = computeElementPosition(link ? node.parentElement : node, containerEl);
    // Get dimension
    const boundingBox = node.getBBox();
    const dimension = {
        width: boundingBox.width,
        height: boundingBox.height,
    };
    // Extract style
    const labelContainerStyleText = node
        .querySelector(".label-container")
        ?.getAttribute("style");
    const labelStyleText = node.querySelector(".label")?.getAttribute("style");
    const containerStyle = {};
    labelContainerStyleText?.split(";").forEach((property) => {
        if (!property) {
            return;
        }
        const key = property.split(":")[0].trim();
        const value = cleanCSSValue(property.split(":")[1] || "");
        if (value) {
            containerStyle[key] = value;
        }
    });
    const labelStyle = {};
    labelStyleText?.split(";").forEach((property) => {
        if (!property) {
            return;
        }
        const key = property.split(":")[0].trim();
        const value = cleanCSSValue(property.split(":")[1] || "");
        if (value) {
            labelStyle[key] = value;
        }
    });
    if (vertex.classes && classes instanceof Map) {
        const classDef = classes.get(Array.isArray(vertex.classes) ? vertex.classes[0] : vertex.classes);
        if (classDef) {
            classDef.styles?.forEach((style) => {
                const [key, value] = style.split(":");
                const cleanedValue = cleanCSSValue(value || "");
                if (cleanedValue) {
                    containerStyle[key.trim()] = cleanedValue;
                }
            });
            classDef.textStyles?.forEach((style) => {
                const [key, value] = style.split(":");
                const cleanedValue = cleanCSSValue(value || "");
                if (cleanedValue) {
                    labelStyle[key.trim()] = cleanedValue;
                }
            });
        }
    }
    return {
        id: vertex.id,
        labelType: vertex.labelType,
        text: entityCodesToText(vertex.text || ""),
        type: vertex.type,
        link: link || undefined,
        ...position,
        ...dimension,
        containerStyle,
        labelStyle,
    };
};
const parseEdge = (edge, edgeIndex, containerEl) => {
    // Find edge element
    const node = containerEl.querySelector(`[id*="${edge.id}"]`);
    if (!node) {
        throw new Error("Edge element not found");
    }
    // Compute edge position data
    const position = computeElementPosition(node, containerEl);
    const edgePositionData = computeEdgePositions(node, position);
    // Remove irrelevant properties
    edge.length = undefined;
    return {
        ...edge,
        ...edgePositionData,
        text: entityCodesToText(edge.text),
    };
};
// Compute element position
const computeElementPosition = (el, containerEl) => {
    if (!el) {
        throw new Error("Element not found");
    }
    let root = el.parentElement?.parentElement;
    const childElement = el.childNodes[0];
    let childPosition = { x: 0, y: 0 };
    if (childElement) {
        const { transformX, transformY } = getTransformAttr(childElement);
        const boundingBox = childElement.getBBox();
        childPosition = {
            x: Number(childElement.getAttribute("x")) ||
                transformX + boundingBox.x ||
                0,
            y: Number(childElement.getAttribute("y")) ||
                transformY + boundingBox.y ||
                0,
        };
    }
    const { transformX, transformY } = getTransformAttr(el);
    const position = {
        x: transformX + childPosition.x,
        y: transformY + childPosition.y,
    };
    while (root && root.id !== containerEl.id) {
        if (root.classList.value === "root" && root.hasAttribute("transform")) {
            const { transformX, transformY } = getTransformAttr(root);
            position.x += transformX;
            position.y += transformY;
        }
        root = root.parentElement;
    }
    return position;
};
export const parseMermaidFlowChartDiagram = (db, containerEl) => {
    // Get mermaid parsed data from the diagram's database (Mermaid v11 API)
    const verticesData = db.getVertices();
    const edgesData = db.getEdges();
    const subGraphsData = db.getSubGraphs();
    const classesData = db.getClasses();
    // Parse vertices
    const vertices = {};
    // Normalize classesData - it can be a Map or empty object {}
    const normalizedClasses = classesData instanceof Map ? classesData : {};
    // In v11, getVertices() returns a Map
    if (verticesData instanceof Map) {
        verticesData.forEach((vertex, id) => {
            vertices[id] = parseVertex(vertex, containerEl, normalizedClasses);
        });
    }
    else if (typeof verticesData === "object" && verticesData !== null) {
        // Fallback for object-based return
        Object.entries(verticesData).forEach(([id, vertex]) => {
            vertices[id] = parseVertex(vertex, containerEl, normalizedClasses);
        });
    }
    // Track the count of edges based on the edge id
    const edgeCountMap = new Map();
    const edges = (Array.isArray(edgesData) ? edgesData : [])
        .map((edge) => {
        // Filter out edges not found in the DOM
        if (!containerEl.querySelector(`[id*="${edge.id}"]`)) {
            return null;
        }
        // Calculate index for edges between the same two nodes
        const edgeMapKey = `${edge.start}-${edge.end}`;
        const count = edgeCountMap.get(edgeMapKey) || 0;
        edgeCountMap.set(edgeMapKey, count + 1);
        return parseEdge(edge, count, containerEl);
    })
        .filter((edge) => edge !== null && edge.reflectionPoints.length > 1);
    const subGraphs = (Array.isArray(subGraphsData) ? subGraphsData : []).map((subgraph) => {
        return parseSubGraph(subgraph, containerEl);
    });
    return {
        type: "flowchart",
        subGraphs,
        vertices,
        edges,
    };
};
//# sourceMappingURL=flowchart.js.map